'use client';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alert,
  AlertDescription,
} from '@/shared/components/ui/alert';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs';

import {
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Loader2,
  LogIn,
  Mail,
  UserPlus,
} from 'lucide-react';

import type {
  AppDispatch,
  RootState,
} from '@/store/store';

import {
  clearError,
  clearForgotPasswordState,
  closeLoginDialog,
} from '../store/authSlice';

import { useAuthForm } from '../hooks/useAuthForm';

import LoginForm from './LoginForm';
import SignupForm from './SignupForm';

type AuthView =
  | 'login'
  | 'signup'
  | 'forgot-password';

const DIALOG_TITLE = 'Sign In';

const DIALOG_DESCRIPTION =
  'Sign in to continue to your account.';

export default function LoginDialog() {
  const dispatch = useDispatch<AppDispatch>();

  const {
    loginDialogOpen,
    authMode,
    pendingJobId,
    error: authError,
    forgotPasswordSuccess,
  } = useSelector(
    (state: RootState) => state.auth
  );

  const [mode, setMode] = useState<
    'login' | 'signup'
  >(authMode);

  const [view, setView] =
    useState<AuthView>('login');

  const [
    forgotPasswordEmail,
    setForgotPasswordEmail,
  ] = useState('');

  const closeDialog = () => {
    dispatch(closeLoginDialog());
    dispatch(clearForgotPasswordState());

    setView('login');
    setForgotPasswordEmail('');
  };

  const {
    error,
    isSubmitting,
    submitLogin,
    submitSignup,
    submitForgotPassword,
    resetError,
  } = useAuthForm({
    pendingJobId,
    onSuccess: closeDialog,
  });

  useEffect(() => {
    if (!loginDialogOpen) {
      return;
    }

    resetError();

    setMode(authMode);
    setView('login');
    setForgotPasswordEmail('');

    dispatch(clearError());
    dispatch(clearForgotPasswordState());
  }, [
    loginDialogOpen,
    authMode,
    dispatch,
    resetError,
  ]);

  const handleForgotPassword = () => {
    resetError();

    dispatch(clearError());
    dispatch(clearForgotPasswordState());

    setForgotPasswordEmail('');
    setView('forgot-password');
  };

  const handleBackToLogin = () => {
    resetError();

    dispatch(clearError());
    dispatch(clearForgotPasswordState());

    setView('login');
  };

  const handleForgotPasswordSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    try {
      await submitForgotPassword(
        forgotPasswordEmail
      );
    } catch {
      return;
    }
  };

  return (
    <Dialog
      open={loginDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeDialog();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-md overflow-y-auto rounded-2xl p-5 sm:p-6">
        {view === 'forgot-password' ? (
          <>
            <DialogHeader className="items-center text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <KeyRound className="h-7 w-7 text-primary" />
              </div>

              <DialogTitle className="text-xl font-semibold">
                Forgot Password?
              </DialogTitle>

              <DialogDescription className="max-w-sm text-sm leading-5 text-muted-foreground">
                No worries. Enter your registered
                email address and we'll send you a
                secure password reset link.
              </DialogDescription>
            </DialogHeader>

            {(error || authError) && (
              <Alert
                variant="destructive"
                className="mt-4"
              >
                <AlertDescription>
                  {error ?? authError}
                </AlertDescription>
              </Alert>
            )}

            {forgotPasswordSuccess ? (
              <div className="mt-5 rounded-xl border bg-muted/30 p-5 text-center">
                <div className="flex justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <h3 className="font-semibold">
                    Check your email
                  </h3>

                  <p className="text-sm leading-5 text-muted-foreground">
                    If an account exists with this
                    email address, we've sent a
                    password reset link.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form
                onSubmit={
                  handleForgotPasswordSubmit
                }
                className="mt-5 space-y-5"
              >
                <div className="space-y-2">
                  <label
                    htmlFor="forgot-password-email"
                    className="text-sm font-medium"
                  >
                    Email Address
                  </label>

                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                    <input
                      id="forgot-password-email"
                      type="email"
                      placeholder="you@company.com"
                      value={
                        forgotPasswordEmail
                      }
                      onChange={(e) => {
                        setForgotPasswordEmail(
                          e.target.value
                        );
                        resetError();
                      }}
                      required
                      disabled={isSubmitting}
                      autoComplete="email"
                      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 pl-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex h-11 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending Reset Link...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send Reset Link
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  disabled={isSubmitting}
                  className="flex w-full items-center justify-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <DialogHeader className="text-center">
              <DialogTitle className="text-xl">
                {DIALOG_TITLE}
              </DialogTitle>

              <DialogDescription className="text-sm text-muted-foreground">
                {DIALOG_DESCRIPTION}
              </DialogDescription>
            </DialogHeader>

            {(error || authError) && (
              <Alert
                variant="destructive"
                className="mt-2"
              >
                <AlertDescription>
                  {error ?? authError}
                </AlertDescription>
              </Alert>
            )}

            <Tabs
              value={mode}
              onValueChange={(value) => {
                setMode(
                  value as
                    | 'login'
                    | 'signup'
                );

                resetError();
              }}
              className="mt-2"
            >
              <TabsList className="grid h-11 w-full grid-cols-2">
                <TabsTrigger
                  value="login"
                  className="gap-2"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </TabsTrigger>

                <TabsTrigger
                  value="signup"
                  className="gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Sign Up
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <LoginForm
                  onSubmit={submitLogin}
                  onForgotPassword={
                    handleForgotPassword
                  }
                  isSubmitting={isSubmitting}
                />
              </TabsContent>

              <TabsContent value="signup">
                <SignupForm
                  onSubmit={submitSignup}
                  isSubmitting={isSubmitting}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}